/* Voice input, shared by every place someone answers a question in their own
   words: the AI interview, the gap questions Triton sends a client, and the
   Expert Studio FAQs.

   Extracted rather than copied because the awkward parts are browser bugs — see
   the comments inside — and three copies of a workaround drift apart. */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/icons';
import { startDictation, applyResult, endRun, type DictationState, type RecognitionResult } from '@/lib/dictation-merge';
import { WhisperDictation, recordingSupported } from '@/lib/dictation-whisper';
import { useClientAccess } from '@/auth/useClientAccess';

// Minimal typing for the browser SpeechRecognition API (not in lib.dom yet).
export type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
};

// Turn a SpeechRecognition error code into a plain, actionable message. Safari in
// particular needs system Dictation enabled and often reports 'service-not-allowed'.
function micErrorMessage(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone/voice access is blocked. Allow the mic for this site — on Safari also enable Dictation in System Settings ▸ Keyboard — or just type your answer.';
    case 'audio-capture':
      return 'No microphone was found. Check your mic, or type your answer instead.';
    case 'network':
      return 'The voice service is unavailable right now. Please type your answer.';
    default:
      return 'Voice input stopped unexpectedly. You can tap the mic to retry, or type your answer.';
  }
}
export function getSpeechRecognition(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

function flatten(results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>): RecognitionResult[] {
  const out: RecognitionResult[] = [];
  for (let i = 0; i < results.length; i++) out.push({ transcript: results[i][0]?.transcript ?? '', isFinal: results[i].isFinal });
  return out;
}

/* TEMPORARY — Phase 0 of the dictation work. With `?dictation-debug` in the
   URL, every recognition event is recorded and copied to the clipboard when the
   mic is stopped, so a client whose phone doubles phrases can send us the raw
   stream to replay in tests/dictation-merge.test.ts. Remove once the Whisper
   engine has replaced this path. */
type DebugEvent = { t: number; run: number; type: string; resultIndex?: number; results?: RecognitionResult[]; error?: string };
const debugOn = () => typeof window !== 'undefined' && /[?&]dictation-debug/.test(window.location.search);

/* Dictation shared by the AI interview, the client's gap answers and the
   Expert Studio FAQs. One hook, two engines behind it:

   - `whisper` records the mic and sends each phrase to the Worker, which
     transcribes it on Workers AI and tidies the result when the mic stops
     (src/lib/dictation-whisper.ts). Chosen by the rollout switch on /api/me.
   - `browser` is the device's own SpeechRecognition. What ends up in the box
     is decided by the pure reducer in dictation-merge.ts; this hook only
     drives the browser. The two rules that matter here, both browser bugs:
     every run gets a FRESH recognizer (restarting the same one on iOS
     re-emits the last phrase), and every handler carries its run's token so
     an event that arrives after the run ended cannot touch the text. Safari
     ends recognition after every phrase and Chrome times out around a
     minute, so a run ending while the user still wants the mic on just
     starts the next one.

   Call sites never know which engine they got. `pending` is true while a
   phrase is still being transcribed or tidied — the box should not accept
   typing then, or the tidy result would overwrite it. */
export function useDictation(onText: (next: string) => void, opts: { auth?: boolean } = {}) {
  const { access } = useClientAccess({ enabled: opts.auth !== false });
  const engine: 'browser' | 'whisper' = access?.dictationEngine === 'whisper' && recordingSupported() ? 'whisper' : 'browser';
  const [listening, setListening] = useState(false);
  const [pending, setPending] = useState(false);
  /** Whisper only: how loud the speaker is above the room, 0..1. Undefined on the browser engine, which reports no level. */
  const [level, setLevel] = useState<number | undefined>(undefined);
  const [micError, setMicError] = useState<string | null>(null);
  const [browserOk] = useState(() => !!getSpeechRecognition());
  const supported = engine === 'whisper' || browserOk;
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const whisperRef = useRef<WhisperDictation | null>(null);
  const wantListeningRef = useRef(false);  // user wants the mic on — drives auto-restart
  const stateRef = useRef<DictationState>(startDictation(''));
  const lastResultsRef = useRef<RecognitionResult[]>([]);  // the current run's latest list, for endRun
  const debugRef = useRef<DebugEvent[]>([]);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  const lastTextRef = useRef('');            // what this hook last put in the box
  const emit = (t: string) => { lastTextRef.current = t; onTextRef.current(t); };
  const endedRef = useRef<((text: string) => void) | null>(null);  // resolves stop() on the browser engine

  const log = (e: Omit<DebugEvent, 't'>) => { if (debugOn()) debugRef.current.push({ t: Math.round(performance.now()), ...e }); };

  /** Stops the mic and resolves with the text the box holds once the last
      words are in — after the final chunk and tidy on Whisper, after the
      browser's final result on its engine. Send awaits it. */
  const stop = (): Promise<string> => {
    if (whisperRef.current) {
      const w = whisperRef.current;
      whisperRef.current = null;
      setListening(false);
      return w.stop();
    }
    if (!wantListeningRef.current && !recognitionRef.current) return Promise.resolve(lastTextRef.current);
    wantListeningRef.current = false;
    const ended = new Promise<string>((res) => {
      endedRef.current = res;
      // Some browsers never fire onend after stop(); do not leave Send waiting.
      window.setTimeout(() => res(lastTextRef.current), 1500);
    });
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    setListening(false);
    if (debugOn() && debugRef.current.length) {
      const json = JSON.stringify(debugRef.current);
      navigator.clipboard?.writeText(json).then(
        () => window.alert('Dictation debug log copied to the clipboard — paste it into an email to Triton.'),
        () => console.log('dictation-debug', json),
      );
    }
    return ended;
  };

  /** Start one recognition run. Called again from onend while the mic is wanted. */
  const startRun = (): boolean => {
    const rec = getSpeechRecognition();
    if (!rec) return false;
    const run = stateRef.current.run;
    rec.lang = 'en-US';
    rec.interimResults = true;  // words appear live as you speak
    rec.continuous = true;      // keeps listening through pauses (ignored by Safari)
    lastResultsRef.current = [];

    rec.onresult = (e) => {
      const results = flatten(e.results);
      log({ run, type: 'result', resultIndex: e.resultIndex, results });
      if (run !== stateRef.current.run) return;  // straggler from a closed run
      lastResultsRef.current = results;
      const r = applyResult(stateRef.current, run, results);
      stateRef.current = r.state;
      emit(r.text);
    };
    rec.onerror = (e) => {
      const code = e?.error || '';
      log({ run, type: 'error', error: code });
      // 'no-speech' / 'aborted' happen on ordinary pauses — let onend restart.
      if (code && code !== 'no-speech' && code !== 'aborted') {
        wantListeningRef.current = false;
        setMicError(micErrorMessage(code));
      }
    };
    rec.onend = () => {
      log({ run, type: 'end' });
      if (run !== stateRef.current.run) return;
      stateRef.current = endRun(stateRef.current, run, lastResultsRef.current);
      if (wantListeningRef.current) {
        if (!startRun()) { wantListeningRef.current = false; setListening(false); }
      } else {
        recognitionRef.current = null;
        setListening(false);
        endedRef.current?.(lastTextRef.current);
        endedRef.current = null;
      }
    };
    recognitionRef.current = rec;
    try { rec.start(); log({ run, type: 'start' }); return true; }
    catch { return false; }
  };

  const start = (current: string) => {
    setMicError(null);
    if (engine === 'whisper') {
      const w = new WhisperDictation({ onText: emit, onPending: setPending, onError: setMicError, onLevel: setLevel });
      whisperRef.current = w;
      w.start(current).then(() => setListening(true), () => { whisperRef.current = null; setListening(false); });
      return;
    }
    if (!browserOk) {
      setMicError('Voice input isn’t supported in this browser. Please type your answer.');
      return;
    }
    stateRef.current = startDictation(current);
    lastTextRef.current = current;
    debugRef.current = [];
    wantListeningRef.current = true;
    if (startRun()) {
      setListening(true);
    } else {
      wantListeningRef.current = false;
      setListening(false);
      setMicError('Could not start voice input. Please type your answer.');
    }
  };

  // Never let the auto-restart loop or an open mic outlive the component that owns it.
  useEffect(() => () => {
    wantListeningRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    void whisperRef.current?.stop();
  }, []);

  return { listening, pending, level, micError, supported, engine, start, stop };
}

/** Five bars that move with the voice — the cue that the mic hears you. With
    no level (the browser engine reports none) they pulse on their own. */
export function LevelBars({ level }: { level?: number }) {
  const shape = [0.55, 0.8, 1, 0.8, 0.55];
  return (
    <span className={`cq-level ${level === undefined ? 'cq-level--pulse' : ''}`} aria-hidden="true">
      {shape.map((k, i) => <i key={i} style={level === undefined ? undefined : { height: `${Math.round(18 + 82 * Math.min(1, level * k * 1.4))}%` }} />)}
    </span>
  );
}

/** The mic button, same chrome and icon the interview composer uses. While
    listening it shows the level bars, so a glance says the mic is live. */
export function MicButton({ listening, level, onToggle }: { listening: boolean; level?: number; onToggle: () => void }) {
  return (
    <button type="button" className={`cq-mic cq-mic--sm ${listening ? 'is-on' : ''}`} onClick={onToggle}
      aria-label={listening ? 'Stop dictation' : 'Dictate'}
      title={listening ? 'Stop — then check the text' : 'Speak your answer'}>
      {listening ? <LevelBars level={level} /> : <Icon name="mic" size={14} />}
    </button>
  );
}
